import cloneDeep from 'lodash.clonedeep';
import { QueryBuilderModel } from 'vet-bones/components';
import {
  INSTANCE_SPLIT_SYMBOL,
  OperatorType,
  QueryBuilder,
  Variable,
  encodeRule,
} from 'vet-bones/utils/queryBuilder';

import { ModelRule } from 'src/containers/ProjectDiagram/types/data';
import { isRuleValid } from 'src/containers/ProjectDiagram/utils/rules/isRuleValid';
import { RulesInfo } from 'src/containers/PublishProjectDrawer/types';
import {
  DESIGNER_RULE_CONTENT_IRI,
  DESIGNER_RULE_DATA_IRI,
  RDFS_LABEL_IRI,
  RULE_CONTENT_IRI,
  RULE_SPARQL_RULE_IRI,
} from 'src/templates/queries/iris';
import { escapeDoubleQuotes } from 'src/utils/escapeDoubleQuotes';
import { getAbbreviationOrFullIri } from 'src/utils/getAbbreviationOrFullIri';
import { replaceInvalidIriChars } from 'src/utils/removeInvalidIriChars';
import { buildIfClause } from 'src/utils/stringify/rules/buildIfClause';
import { buildThenClause } from 'src/utils/stringify/rules/buildThenClause';
import { getRuleVariable } from 'src/utils/stringify/rules/getRuleVariable';

const buildRule = ({
  ruleIri,
  ruleName,
  abbreviate,
  getVariable,
  variablesById,
  instancesByIds,
  ifClause,
  thenClause,
}: {
  ruleIri: string;
  ruleName: string;
  abbreviate: (iri: string) => string;
  getVariable: (variable: string, id?: string) => string;
  variablesById: Record<string, string>;
  instancesByIds: Array<Record<string, string>>;
  ifClause: QueryBuilder;
  thenClause: QueryBuilder;
}) => {
  const ruleContent = `${buildIfClause({
    topExpression: ifClause.data.query,
    abbreviate,
    getVariable,
    variablesById,
    instancesByIds,
  })}\n${buildThenClause({
    topExpression: thenClause.data.query,
    abbreviate,
    getVariable,
    instancesByIds,
  })}`;

  return `${ruleIri} a ${abbreviate(RULE_SPARQL_RULE_IRI)} ;
  ${abbreviate(RDFS_LABEL_IRI)} "${escapeDoubleQuotes(ruleName)}" ;
  ${abbreviate(RULE_CONTENT_IRI)} """${
    escapeDoubleQuotes(ruleContent) // must use triple quotes for new line char
  }""" ;
  ${abbreviate(DESIGNER_RULE_CONTENT_IRI)} """${
    escapeDoubleQuotes(ruleContent) // must use triple quotes for new line char
  }""" ;
  ${abbreviate(DESIGNER_RULE_DATA_IRI)} "${escapeDoubleQuotes(
    encodeRule([ifClause, thenClause]) || ''
  )}" .
`;
};

export const stringifyRule = (
  rule: ModelRule,
  namespaces: Record<string, string>
) => {
  const { getVariable, variablesById } = getRuleVariable();
  const abbreviate = (iri: string) => getAbbreviationOrFullIri(iri, namespaces);

  const baseRuleIri = `_:${replaceInvalidIriChars(rule.id)}`;

  // we only allow IN operator in if clause
  const allInstancesById = rule.ifClause.data.query.expressions.reduce<
    Record<string, string[]>
  >((acc, subExpression) => {
    subExpression.expressions.forEach((expression) => {
      expression.filters.forEach((filter) => {
        if (filter.operator.operatorType === OperatorType.INSTANCE) {
          const iris = (filter.value as string)
            .split(INSTANCE_SPLIT_SYMBOL)
            .filter(Boolean)
            .map((iri) => iri.replace(/`/g, ''));

          acc[filter.subject.id] = iris;
        }
      });
    });
    return acc;
  }, {});

  // we do not want to allow multiple instances in the then clause, so we split them out
  const splitThenClauseIds = rule.thenClause.data.query.expressions.reduce<
    Set<string>
  >((acc, subExpression) => {
    const addInstanceId = ({ id }: Variable) => {
      if (allInstancesById[id]?.length > 1) {
        acc.add(id);
      }
    };

    subExpression.expressions.forEach((expression) => {
      expression.filters.forEach((filter) => {
        addInstanceId(filter.subject);

        if (filter.value && typeof filter.value !== 'string') {
          addInstanceId(filter.value);
        }
      });
    });
    return acc;
  }, new Set());

  // if there are no instances in the then clause with more than one value,
  // use the initial rule iri and name directly
  if (!splitThenClauseIds.size) {
    const instancesByIds: Array<Record<string, string>> = [{}];
    Object.entries(allInstancesById).forEach(([id, iris]) => {
      const recordsToCopy = [...instancesByIds];
      iris.forEach((iri, idx) => {
        if (!idx) {
          instancesByIds.forEach((instancesById) => {
            // eslint-disable-next-line no-param-reassign
            instancesById[id] = iri;
          });
        } else {
          recordsToCopy.forEach((record) => {
            instancesByIds.push({ ...record, [id]: iri });
          });
        }
      });
    });

    return buildRule({
      ruleIri: baseRuleIri,
      ruleName: rule.name,
      ifClause: rule.ifClause,
      thenClause: rule.thenClause,
      abbreviate,
      getVariable,
      variablesById,
      instancesByIds,
    });
  }

  // otherwise, split these up into multiple rules and initialize a base record
  // of instancesById for each combination of single instance values
  const baseInstancesByIds: Array<Record<string, string>> = [{}];
  splitThenClauseIds.forEach((id) => {
    const recordsToCopy = [...baseInstancesByIds];
    allInstancesById[id].forEach((iri, idx) => {
      if (!idx) {
        baseInstancesByIds.forEach((instancesById) => {
          // eslint-disable-next-line no-param-reassign
          instancesById[id] = iri;
        });
      } else {
        recordsToCopy.forEach((record) => {
          baseInstancesByIds.push({ ...record, [id]: iri });
        });
      }
    });
  });

  return baseInstancesByIds
    .map((baseInstancesById, idx) => {
      const instancesByIds: Array<Record<string, string>> = [
        { ...baseInstancesById },
      ];
      Object.entries(allInstancesById).forEach(([id, iris]) => {
        if (baseInstancesById[id]) {
          return;
        }
        const recordsToCopy = [...instancesByIds];
        iris.forEach((iri, idx) => {
          if (!idx) {
            instancesByIds.forEach((instancesById) => {
              // eslint-disable-next-line no-param-reassign
              instancesById[id] = iri;
            });
          } else {
            recordsToCopy.forEach((record) => {
              instancesByIds.push({ ...record, [id]: iri });
            });
          }
        });
      });

      // remove the multiple instance values from the if clause data that is saved
      const newIfClause = cloneDeep(rule.ifClause);
      newIfClause.data.query.expressions.forEach((subExpression) => {
        subExpression.expressions.forEach((expression) => {
          expression.filters.forEach((filter) => {
            if (filter.operator.operatorType === OperatorType.INSTANCE) {
              const subjectId = filter.subject.id;
              const singleValue = baseInstancesById[subjectId];
              if (singleValue) {
                // eslint-disable-next-line no-param-reassign
                filter.value = `\`${singleValue}\``;
              }
            }
          });
        });
      });

      return buildRule({
        ruleIri: `${baseRuleIri}_${idx + 1}`,
        ruleName: `${rule.name} (${idx + 1})`,
        ifClause: newIfClause,
        thenClause: rule.thenClause,
        abbreviate,
        getVariable,
        variablesById,
        instancesByIds,
      });
    })
    .join('\n\n');
};

export const stringifyRules = (
  model: QueryBuilderModel,
  ruleInfo: RulesInfo,
  namespaces: Record<string, string>
) => {
  const validRules = Object.values(ruleInfo).filter((rule) =>
    isRuleValid(rule, model)
  );

  return validRules.map((rule) => stringifyRule(rule, namespaces)).join('\n\n');
};
